'use client';

import React from 'react';
import Image from 'next/image';
import { useBranding } from '@/providers/branding-provider';

interface LogoProps {
  className?: string;
  showName?: boolean;
  showTagline?: boolean;
  size?: 'sm' | 'md' | 'lg';
  useSvgImage?: boolean;
}

export function Logo({
  className = '',
  showName = true,
  showTagline = false,
  size = 'md',
  useSvgImage = false,
}: LogoProps) {
  const { branding } = useBranding();
  const brandName = branding?.name || 'Zeric.link';
  const logoUrl = branding?.logoUrl || '/zeric-logo.svg';

  const iconSizes = {
    sm: 'size-7',
    md: 'size-9',
    lg: 'size-11',
  };

  const textSizes = {
    sm: 'text-base',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  const imageSizes = {
    sm: { w: 120, h: 28 },
    md: { w: 150, h: 36 },
    lg: { w: 180, h: 42 },
  };

  // If custom uploaded logo or useSvgImage requested
  const isCustomLogo = logoUrl && !logoUrl.includes('/zeric-logo.svg') && !logoUrl.includes('/zeric-mark.svg');

  if (useSvgImage || (isCustomLogo && showName)) {
    return (
      <div className={`flex items-center select-none ${className}`}>
        <Image
          src={logoUrl}
          alt={brandName}
          width={imageSizes[size].w}
          height={imageSizes[size].h}
          className="h-auto w-auto max-h-9 object-contain"
          priority
        />
      </div>
    );
  }

  // Parse name and extension if applicable
  const nameParts = brandName.includes('.') ? brandName.split('.') : [brandName];
  const mainName = nameParts[0];
  const extName = nameParts.length > 1 ? `.${nameParts.slice(1).join('.')}` : null;

  return (
    <div className={`flex items-center gap-2.5 select-none ${className}`}>
      {/* Zeric Brand Icon Mark */}
      <div
        className={`relative flex items-center justify-center ${iconSizes[size]} rounded-xl bg-gradient-to-br from-slate-900 via-[#0d1c24] to-[#041318] p-1.5 border border-emerald-500/30 shadow-md shadow-emerald-500/10 group-hover:border-emerald-400 transition-all shrink-0`}
      >
        <svg
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
        >
          <defs>
            <linearGradient id="logoZericGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="50%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
          <path
            d="M 18 20 C 18 18.5 19.5 17 21 17 L 43 17 C 45 17 46 19 45 20.5 L 42 25 C 41.5 26 40 27 38.5 27 L 29 27 L 44 39 C 45.5 40 45.5 42.5 44 44 C 43 45 41.5 45.5 40 45.5 L 20 45.5 C 18 45.5 17 43.5 18 42 L 21 37.5 C 21.5 36.5 23 35.5 24.5 35.5 L 35 35.5 L 20 24 C 18.5 22.5 18 21.5 18 20 Z"
            fill="url(#logoZericGrad)"
          />
          <circle cx="46" cy="18" r="2.5" fill="#38bdf8" />
          <circle cx="17" cy="45" r="2.5" fill="#10b981" />
        </svg>
      </div>

      {showName && (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span
              className={`font-black ${textSizes[size]} tracking-tight bg-gradient-to-r from-slate-900 via-emerald-800 to-teal-700 bg-clip-text text-transparent dark:from-white dark:via-slate-100 dark:to-emerald-300`}
            >
              {mainName}
            </span>
            {extName && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                {extName}
              </span>
            )}
          </div>
          {showTagline && (
            <span className="text-[9px] font-semibold tracking-wider uppercase text-muted-foreground">
              Let&apos;s sale automatic
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default Logo;
